import { apiClient } from '@/api/client';
import { queryKeys } from '@/lib/queryKeys';
import { useQuery } from '@tanstack/react-query';

// Growth metrics
export interface GrowthStats {
  plansThisWeek: number;
  plansLastWeek: number;
  plansThisMonth: number;
  plansLastMonth: number;
  weeklyGrowth: number;
  monthlyGrowth: number;
  newUsersThisWeek: number;
  newBusinessesThisWeek: number;
}

// User engagement metrics
export interface EngagementStats {
  activeUsers7d: number;
  activeUsers30d: number;
  avgPlansPerUser7d: number;
  avgPlansPerUser30d: number;
  repeatUsers: number;
  powerUsers: number;
}

export interface PlanStateStats {
  upcoming: number;
  now: number;
  past: number;
  deleted: number;
  avgDurationHours: number;
}

// Geographic demand
export interface GeographicStats {
  city: string;
  country: string;
  planCount: number;
  businessCount: number;
  plansPerBusiness: number;
}

// Top business
export interface TopBusiness {
  id: number;
  name: string;
  city: string | null;
  planCount: number;
}

// Booking patterns
export interface DayOfWeekStats {
  day: string;
  count: number;
}

export interface BookingPatterns {
  byDayOfWeek: DayOfWeekStats[];
  avgDaysInAdvance: number;
  sameDayPercentage: number;
  peakDay: string;
  peakHour: number;
}

export interface RecentPlan {
  id: number;
  user: {
    id: number;
    firstName: string | null;
    lastName: string | null;
  };
  business: {
    id: number;
    name: string;
  };
  startAt: string;
  createdAt: string;
  state: 'upcoming' | 'now' | 'past';
}

// Complete analytics response
export interface PlatformAnalytics {
  growth: GrowthStats;
  engagement: EngagementStats;
  planState: PlanStateStats;
  geographic: GeographicStats[];
  topBusinesses: TopBusiness[];
  businessesWithNoPlans: number;
  bookingPatterns: BookingPatterns;
  recentPlans: RecentPlan[];
}

export const usePlatformAnalytics = () => {
  return useQuery({
    queryKey: [queryKeys.ADMIN_ANALYTICS, 'overview'],
    queryFn: async (): Promise<PlatformAnalytics> => {
      const response = await apiClient.GET('/admin/analytics/overview');
      if (response.error) throw new Error('Failed to fetch analytics');
      return response.data as PlatformAnalytics;
    },
    staleTime: 30000, // 30 seconds
    refetchOnWindowFocus: true,
  });
};
